import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';

import { useQuery, useMutation } from '@apollo/client';
import { formatNumber, isValidPhoneNumber, parsePhoneNumberWithError } from 'libphonenumber-js';
import { toast } from 'react-toastify';

// Our Own imports (absolute)
import { ChatIcon, EmailIcon, PhoneIcon } from '../../atoms/Icons';
import LoadingSkeleton from '../../atoms/LoadingSkeleton';
import useUser from '../../utils/hooks/useUser';

// Relative path imports (./)
import TransportMethodOption from './TransportMethodOption';
import useTransportTesting from './useTransportTesting.jsx';
import { mapTransportStatus, pickMostImportantNotificationStatus } from '../../utils/transportStatus';
import {
  GET_USER_CONTACT_INFO_GQL,
  GET_USER_NOTIFICATION_TRANSPORT_SETTINGS_GQL,
  GET_MOST_RECENT_NOTIFICATION_STATUSES_GQL,
  UPSERT_USER_NOTIFICATION_TRANSPORT_SETTINGS_GQL,
} from './queries';
import './TransportMethodSelector.scss';

const FEEDFLO_SMS_NUMBER = '(204) 262-1928';
const phoneNumberDigits = FEEDFLO_SMS_NUMBER.replace(/\D/g, '');

// NOTE: This order should match the TransportStatusBadges.jsx ordering
const TRANSPORT_OPTIONS = [
  { id: 'sms', name: 'SMS', icon: ChatIcon, description: '' },
  { id: 'push', name: 'Mobile App', icon: PhoneIcon, description: '' },
  { id: 'email', name: 'Email', icon: EmailIcon, description: '' },
];

function TransportMethodSelector(
  {
    onChange = () => { },
    disabled = false,
    label = 'Notification Methods',
    showDetails = false,
    userId: propUserId,
    onBeforeTest,
    autoSave = false,
    smsTestDisabled = false,
  },
  ref,
) {
  // --- Hooks ---
  const { user: loggedInUser } = useUser();
  const userId = propUserId || loggedInUser?.id;

  // --- State hooks ---
  const [selectedMethods, setSelectedMethods] = useState([]);
  const [transportStatus, setTransportStatus] = useState({});
  const [pendingTests, setPendingTests] = useState({});

  // --- Apollo/GraphQL hooks ---
  const { loading: transportSettingsLoading, data: transportSettingsData } = useQuery(
    GET_USER_NOTIFICATION_TRANSPORT_SETTINGS_GQL,
    {
      variables: { userId },
      context: { headers: { 'x-hasura-role': 'user' } },
      skip: !userId,
      fetchPolicy: 'cache-and-network',
    },
  );

  const { loading: contactInfoLoading, data: contactInfoData } = useQuery(GET_USER_CONTACT_INFO_GQL, {
    variables: { userId },
    context: { headers: { 'x-hasura-role': 'user' } },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  });

  const { loading: notificationStatusesLoading, data: notificationStatusesData } = useQuery(
    GET_MOST_RECENT_NOTIFICATION_STATUSES_GQL,
    {
      variables: {
        userId,
        phoneNumber: contactInfoData?.user?.[0]?.phone_number || '',
        email: contactInfoData?.user?.[0]?.email || '',
      },
      context: { headers: { 'x-hasura-role': 'user' } },
      skip: !userId || !contactInfoData || contactInfoLoading,
      fetchPolicy: 'cache-and-network',
    },
  );

  const [upsertUserNotificationTransportSettings] = useMutation(UPSERT_USER_NOTIFICATION_TRANSPORT_SETTINGS_GQL);

  // --- Custom hooks ---
  const { handleTestTransport, cleanupAllSubscriptions } = useTransportTesting({
    userId,
    onStatusUpdate: setTransportStatus,
    onPendingTestUpdate: setPendingTests,
  });

  // Wrapper for test handler that calls onBeforeTest if provided
  const handleTestWithConfirmation = async (transportId) => {
    if (onBeforeTest) {
      const shouldProceed = await onBeforeTest(transportId, transportStatus[transportId]);
      if (!shouldProceed) return;
    }
    handleTestTransport(transportId);
  };

  // --- Navigation/side effects ---
  useEffect(() => {
    if (transportSettingsData) {
      const selectedMethodsList = Object.keys(transportSettingsData?.user_notification_transport_settings?.[0] || {})
        .filter((key) => ['sms', 'push', 'email'].includes(key))
        .filter((key) => transportSettingsData?.user_notification_transport_settings?.[0]?.[key]);

      setSelectedMethods(selectedMethodsList);
    }
  }, [transportSettingsData]);

  // Initialize transport status with contact info and most recent statuses
  useEffect(() => {
    if (!contactInfoData?.user?.[0] || !notificationStatusesData) return;

    const contactInfo = contactInfoData.user[0];
    const phoneInfo = contactInfo.push_notification_tokens?.[0]?.phone_info;
    const deviceLabel =
      phoneInfo?.manufacturer && phoneInfo?.model ? `${phoneInfo.manufacturer} ${phoneInfo.model}` : null;

    const newTransportStatus = {
      sms: {
        targetAddress: contactInfo.phone_number || 'No phone number on file',
        testDisabled:
          contactInfo.phone_number && isValidPhoneNumber(contactInfo?.phone_number, 'US') ? smsTestDisabled : true,
        sent_at: null,
        status: null,
        configurationWarning: contactInfo.phone_number ? null : 'Phone number needed for SMS notifications',
      },
      email: {
        targetAddress: contactInfo.email || 'No email address found',
        testDisabled: contactInfo.email ? false : true,
        sent_at: null,
        status: null,
        configurationWarning: contactInfo.email ? null : 'Email address needed for email notifications',
      },
      push: {
        targetAddress: deviceLabel || 'No mobile device',
        testDisabled: deviceLabel ? false : true,
        sent_at: null,
        status: null,
        configurationWarning: deviceLabel
          ? null
          : `${!propUserId ? 'You' : contactInfo.name} must allow notifications from the FeedFlo mobile app`,
      },
    };

    if (contactInfo?.phone_number && !isValidPhoneNumber(contactInfo.phone_number, 'US')) {
      newTransportStatus.sms.configurationWarning =
        'Invalid phone number provided. Update and save phone number to continue';
    }

    // Process SMS status
    const smsStatus = pickMostImportantNotificationStatus(
      'sms',
      notificationStatusesData.sms_statuses?.[0]?.sms_notification_statuses,
    );
    if (smsStatus) {
      const smsMappedStatus = mapTransportStatus('sms', smsStatus.status);
      let configurationWarning = null;
      if (smsMappedStatus === 'unsubscribed') {
        configurationWarning = (
          <span>
            {`SMS notifications are currently disabled for this phone number. Text "START" to `}
            <a href={`sms://${phoneNumberDigits}?body=START`} style={{ textDecoration: 'underline' }}>
              {FEEDFLO_SMS_NUMBER}
            </a>
            {` to enable notifications.`}
          </span>
        );
      }

      newTransportStatus.sms = {
        targetAddress: contactInfo.phone_number || 'No phone number found',
        sent_at: smsStatus.occurred_at,
        status: smsMappedStatus,
        configurationWarning: configurationWarning,
        testDisabled:
          !contactInfo.phone_number ||
          (contactInfo?.phone_number && !isValidPhoneNumber(contactInfo.phone_number, 'US')) ||
          smsTestDisabled,
      };
    }

    // Process Email status
    const emailStatus = pickMostImportantNotificationStatus(
      'email',
      notificationStatusesData.email_statuses?.[0]?.email_notification_statuses,
    );
    if (emailStatus) {
      newTransportStatus.email = {
        targetAddress: contactInfo.email || 'No email address found',
        sent_at: emailStatus.occurred_at,
        status: mapTransportStatus('email', emailStatus.status),
        configurationWarning: contactInfo.email ? null : 'Email address needed',
      };
    }

    // Process Push status
    const pushReceipts = notificationStatusesData.push_statuses?.[0]?.push_notification_receipts?.map(
      (receipt) => ({
        ...receipt,
        status: receipt.status,
        occurred_at: receipt.occurred_at,
      }),
    );
    const pushStatus = pickMostImportantNotificationStatus('push', pushReceipts);
    if (pushStatus) {
      const phoneInfo = pushStatus.token?.phone_info;
      let deviceLabel = 'Mobile device';

      if (phoneInfo?.manufacturer && phoneInfo?.model) {
        deviceLabel = `${phoneInfo.manufacturer} ${phoneInfo.model}`;
      } else if (phoneInfo?.manufacturer) {
        deviceLabel = phoneInfo.manufacturer;
      } else if (phoneInfo?.model) {
        deviceLabel = phoneInfo.model;
      }

      newTransportStatus.push = {
        targetAddress: deviceLabel,
        sent_at: pushStatus.occurred_at,
        status: mapTransportStatus('push', pushStatus.status),
        configurationWarning: null, // If there's a push status, they have a device
      };
    }

    setTransportStatus(newTransportStatus);
  }, [contactInfoData, notificationStatusesData, smsTestDisabled]);

  // Cleanup subscriptions on unmount
  useEffect(() => {
    return () => {
      cleanupAllSubscriptions();
    };
  }, []); // Only cleanup on unmount

  // --- Function definitions ---
  const handleToggle = async (methodId) => {
    if (disabled) return;

    const isSelected = selectedMethods.includes(methodId);
    const newMethods = isSelected ? selectedMethods.filter((id) => id !== methodId) : [...selectedMethods, methodId];

    setSelectedMethods(newMethods);
    onChange(true, true);

    // Auto-save if enabled
    if (autoSave) {
      try {
        await upsertUserNotificationTransportSettings({
          variables: {
            user_id: userId,
            sms: newMethods.includes('sms'),
            push: newMethods.includes('push'),
            email: newMethods.includes('email'),
          },
          context: {
            headers: {
              'x-hasura-role': 'user',
            },
          },
        });
        toast.success('Transport settings saved');
      } catch (error) {
        toast.error('Failed to auto-save transport settings');
        console.error('Failed to auto-save transport settings:', error);
        // Revert the change on error
        setSelectedMethods(
          isSelected ? [...selectedMethods, methodId] : selectedMethods.filter((id) => id !== methodId),
        );
      }
    }
  };

  // Expose save method via ref
  useImperativeHandle(ref, () => ({
    save: async () => {
      await upsertUserNotificationTransportSettings({
        variables: {
          user_id: userId,
          sms: selectedMethods.includes('sms'),
          push: selectedMethods.includes('push'),
          email: selectedMethods.includes('email'),
        },
        context: {
          headers: {
            'x-hasura-role': 'user',
          },
        },
      });

      return Promise.resolve();
    },
  }));

  // --- Derived state ---
  const isLoading = transportSettingsLoading || contactInfoLoading || notificationStatusesLoading;

  // --- Render ---
  if (isLoading) {
    return (
      <div className="TransportMethodSelector">
        <div className="TransportMethodSelector-label">
          <LoadingSkeleton className="TransportMethodSelector-labelSkeleton" />
        </div>
        <div className="TransportMethodSelector-options">
          <LoadingSkeleton className="TransportMethodSelector-optionSkeleton" />
          <LoadingSkeleton className="TransportMethodSelector-optionSkeleton" />
          <LoadingSkeleton className="TransportMethodSelector-optionSkeleton" />
        </div>
      </div>
    );
  }

  return (
    <div className="TransportMethodSelector">
      <div className="TransportMethodSelector-label">{label}</div>
      <div className="TransportMethodSelector-options">
        {TRANSPORT_OPTIONS.map((option) => {
          let formattedDetails = transportStatus[option.id];

          if (option.id === 'sms' && typeof formattedDetails?.targetAddress === 'string') {
            try {
              let phoneNumber = parsePhoneNumberWithError(formattedDetails?.targetAddress, 'CA');
              phoneNumber = phoneNumber.formatInternational();
              formattedDetails = {
                ...formattedDetails,
                targetAddress: phoneNumber,
              };
            } catch (e) {
              console.error(e);
            }
          }

          return (
            <TransportMethodOption
              key={option.id}
              id={option.id}
              name={option.name}
              icon={option.icon}
              description={option.description}
              isSelected={selectedMethods.includes(option.id)}
              onToggle={handleToggle}
              onTest={handleTestWithConfirmation}
              details={formattedDetails}
              isPendingTest={!!pendingTests[option.id]}
              disabled={disabled}
              showDetails={showDetails}
            />
          );
        })}
      </div>
    </div>
  );
}

TransportMethodSelector.propTypes = {
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  showDetails: PropTypes.bool,
  userId: PropTypes.string,
  onBeforeTest: PropTypes.func,
  autoSave: PropTypes.bool,
  smsTestDisabled: PropTypes.bool,
};

export default forwardRef(TransportMethodSelector);
